import { createContext, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { Amplify, Auth } from 'aws-amplify';
// import axiosClient from 'src/utils/axios-client';
import { Roles } from 'src/enums/role-based-auth-guard';
import { channelPartnerApi } from 'src/api/newChannelPartner/channelPartner';
import { authApi } from 'src/api/auth';
import { userApi } from 'src/api/user';
import { ResponseCodes } from 'src/enums/responseCodes';
import { amplifyConfig, amplifyTechnicianConfig } from 'src/config';
import type { User } from '../types/user';

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  mobileNumberValid: boolean;
  showVerifyOtp: boolean;
  incorrectOtp: boolean
  user: User | null;
}

interface AuthContextValue extends State {
  method: 'Amplify';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  verifyCode: (username: string, code: string) => Promise<void>;
  resendCode: (username: string) => Promise<void>;
  passwordRecovery: (username: string) => Promise<void>;
  passwordReset: (
    username: string,
    code: string,
    newPassword: string,
    firstLogin: string
  ) => Promise<void>;
  getOtp: (mobileNumber: string) => Promise<void>;
  resetOtpError: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: User;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
};

type VerifyCodeAction = {
  type: 'VERIFY_CODE';
};

type ResendCodeAction = {
  type: 'RESEND_CODE';
}
  ;
type PasswordRecoveryAction = {
  type: 'PASSWORD_RECOVERY';
};

type PasswordResetAction = {
  type: 'PASSWORD_RESET';
};

type GetOtpAction = {
  type: 'GET_OTP';
  payload: {
    mobileNumberValid: boolean,
    showVerifyOtp: boolean,
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | VerifyCodeAction
  | ResendCodeAction
  | PasswordRecoveryAction
  | PasswordResetAction
  | GetOtpAction;

const initialState: State = {
  isAuthenticated: true,
  isInitialized: false,
  mobileNumberValid: true,
  showVerifyOtp: false,
  incorrectOtp: false,
  user: null
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      mobileNumberValid: true,
      showVerifyOtp: false,
      incorrectOtp: false,
      user
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state: State): State => ({ ...state }),
  VERIFY_CODE: (state: State): State => ({ ...state }),
  RESEND_CODE: (state: State): State => ({ ...state }),
  PASSWORD_RECOVERY: (state: State): State => ({ ...state }),
  PASSWORD_RESET: (state: State): State => ({ ...state }),
  GET_OTP: (state: State, action: GetOtpAction): State => (
    { ...state,
      mobileNumberValid: action.payload.mobileNumberValid,
      showVerifyOtp: action.payload.showVerifyOtp,
    }),
};

const reducer = (state: State, action: Action): State => (
  handlers[action.type] ? handlers[action.type](state, action) : state
);

export const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  method: 'Amplify',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verifyCode: () => Promise.resolve(),
  resendCode: () => Promise.resolve(),
  passwordRecovery: () => Promise.resolve(),
  passwordReset: () => Promise.resolve(),
  getOtp: () => Promise.resolve(),
  resetOtpError: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const getCpDetails = async (role, email) => {
    let resultData = {
      id: '',
      name: ''
    };
    if (role === Roles.CHANNEL_PARTNER) {
      const cpResult = await channelPartnerApi.checkCPExistByEmail(email);
      resultData = {
        id: cpResult.responseData?.id,
        name: cpResult.responseData?.name
      };
      return resultData;
    }
    return resultData;
  };

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const tabValue = localStorage.getItem('tab') || 'admin';
        if (tabValue === 'admin') {
          Amplify.configure(amplifyConfig);
        } else {
          Amplify.configure(amplifyTechnicianConfig);
        }
        const userData = await userApi.getUserDetails();
        const userProfile = userData.responseData;
        const user = {
          id: userProfile.UserAttributes.find(
            (e) => e.Name === 'sub'
          )?.Value,
          avatar: userProfile.UserAttributes.find(
            (e) => e.Name === 'picture'
          )?.Value,
          email: userProfile.UserAttributes.find(
            (e) => e.Name === 'email'
          )?.Value,
          name: userProfile.UserAttributes.find(
            (e) => e.Name === 'name'
          )?.Value,
          organization: userProfile.UserAttributes.find(
            (e) => e.Name === 'custom:organization'
          )?.Value,
          first_login: userProfile.UserAttributes.find(
            (e) => e.Name === 'custom:first_login'
          )?.Value,
          scope: userProfile.groups?.[0],
          cpId: '',
          cpName: '',
          technicianId: ''
        };
        if (user.scope === Roles.CHANNEL_PARTNER) {
          const cpData = await getCpDetails(user.scope, user.email);
          user.cpId = cpData.id;
          user.cpName = cpData.name;
        }
        if (user.scope === Roles.TECHNICIAN) {
          user.technicianId = userProfile.UserAttributes.find(
            (e) => e.Name === 'custom:id'
          )?.Value;
        }
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user
          }
        });
      } catch (error) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    const userInfo = await Auth.signIn(email, password);
    if (userInfo.challengeName) {
      console.error(`Unable to login, because challenge "${userInfo.challengeName}" is mandated and we did not handle this case.`);
      return;
    }

    if (userInfo.attributes['custom:first_login'] === 'true') {
      localStorage.clear();
      navigate('/first-login');

      return;
    }

    const userData = await userApi.getUserDetails();
    const userProfile = userData.responseData;
    const user = {
      id: userProfile.UserAttributes.find(
        (e) => e.Name === 'sub'
      )?.Value,
      avatar: userProfile.UserAttributes.find(
        (e) => e.Name === 'picture'
      )?.Value,
      email: userProfile.UserAttributes.find(
        (e) => e.Name === 'email'
      )?.Value,
      name: userProfile.UserAttributes.find(
        (e) => e.Name === 'name'
      )?.Value,
      organization: userProfile.UserAttributes.find(
        (e) => e.Name === 'custom:organization'
      )?.Value,
      first_login: userProfile.UserAttributes.find(
        (e) => e.Name === 'custom:first_login'
      )?.Value,
      scope: userProfile.groups?.[0],
      cpId: '',
      cpName: '',
      technicianId: ''
    };
    if (user.scope === Roles.CHANNEL_PARTNER) {
      const cpData = await getCpDetails(user.scope, user.email);
      user.cpId = cpData.id;
      user.cpName = cpData.name;
    }
    if (user.scope === Roles.TECHNICIAN) {
      user.technicianId = userProfile.UserAttributes.find(
        (e) => e.Name === 'custom:id'
      )?.Value;
    }
    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: true,
        user
      }
    });
  };

  const logout = async (): Promise<void> => {
    await Auth.signOut();
    dispatch({
      type: 'LOGOUT'
    });
  };

  const register = async (email: string, password: string): Promise<void> => {
    await Auth.signUp({
      username: email,
      password,
      attributes: { email }
    });
    dispatch({
      type: 'REGISTER'
    });
  };

  const verifyCode = async (username: string, code: string): Promise<void> => {
    await Auth.confirmSignUp(username, code);
    dispatch({
      type: 'VERIFY_CODE'
    });
  };

  const resendCode = async (username: string): Promise<void> => {
    await Auth.resendSignUp(username);
    dispatch({
      type: 'RESEND_CODE'
    });
  };

  const passwordRecovery = async (username: string): Promise<void> => {
    await Auth.forgotPassword(username);
    dispatch({
      type: 'PASSWORD_RECOVERY'
    });
  };

  const passwordReset = async (
    username: string,
    code: string,
    newPassword: string,
    firstLogin: string
  ): Promise<void> => {
    await Auth.forgotPasswordSubmit(username, code, newPassword);

    if (firstLogin) {
      const user = await Auth.signIn(username, newPassword);
      await Auth.updateUserAttributes(user, {
        'custom:first_login': 'false',
        'custom:authChallenge_req': 'false'
      });
    }

    dispatch({
      type: 'PASSWORD_RESET'
    });
  };

  const resetOtpError = async (): Promise<void> => {
    dispatch({
      type: 'GET_OTP',
      payload: {
        mobileNumberValid: true,
        showVerifyOtp: false,
      }
    });
  };

  const getOtp = async (mobileNumber: string): Promise<void> => {
    const getOtpResponse = await authApi.getTechnicianOtp(mobileNumber);
    if (getOtpResponse.statusCode === ResponseCodes.OK) {
      dispatch({
        type: 'GET_OTP',
        payload: {
          mobileNumberValid: true,
          showVerifyOtp: true,
        }
      });
    } else {
      dispatch({
        type: 'GET_OTP',
        payload: {
          mobileNumberValid: false,
          showVerifyOtp: false,
        }
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'Amplify',
        login,
        logout,
        register,
        verifyCode,
        resendCode,
        passwordRecovery,
        passwordReset,
        getOtp,
        resetOtpError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const AuthConsumer = AuthContext.Consumer;
